import { useMemo } from 'react';
import { InputAdormantHandlerMap, PoiFormState, AllPointOfInterestTemplate } from '@/types';
import { UseTemplateFieldsHookType } from '../hooks/useTemplateFields';
import { TemplateFieldRenderer } from '../shared/TemplateFieldRenderer';
import { Maybe, TemplateFieldTypeEnum, UnitsEnum, UnitSystemEnum } from '@/__generated__/graphql';
import {
  convertInchesToMeters,
  convertMillimetersToMeters,
  localiseMeasurement,
} from '@/utils/unitSystem';
import { unitSystem as unitSystemState } from '@/components/Analysis/state';
import { refreshFields, UNCERTAIN_PROFILE_LABEL, UNCERTAIN_REASON_FIELD_NAME } from '@/constants';
import { useAtomValue } from 'jotai';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import { usePoiDefectData } from '../hooks/usePoiDefectData';
import { useBlisterFieldConfig } from '@/hooks/useBlisterFieldConfig';

type Props = {
  template: AllPointOfInterestTemplate[0] | undefined;
  dataHook: UseTemplateFieldsHookType;
  formState: PoiFormState | undefined;
  inputAdormantHandlerMap: InputAdormantHandlerMap;
  allDisabled?: boolean;
  handleChangeScCategory?: (argument0: string) => void;
  handleChangeControlledField?: (argument0: string, argument1: string) => void;
  excludedFieldKeys?: string[];
};

const filteredFiledNames = new Set(['Comments', UNCERTAIN_REASON_FIELD_NAME]);
const formatValueWithUnit = new Set(['Distance from scanner']);

export const TemplateFieldsForm = ({
  template,
  dataHook,
  formState,
  inputAdormantHandlerMap,
  allDisabled,
  handleChangeControlledField = () => { },
  excludedFieldKeys = [],
}: Props) => {
  const { state, handleChange, blister } = dataHook;
  const unitSystem = useAtomValue(unitSystemState);
  const isEvaluatingFields = useAtomValue(poiState.isEvaluatingFields);
  const blisterFieldConfig = useBlisterFieldConfig({ type: template?.name || '' });

  const { defectLegendKeys } = usePoiDefectData();

  const excludedFieldKeysSet = useMemo(
    () => new Set(excludedFieldKeys.map((k) => k.toLowerCase())),
    [excludedFieldKeys]
  );

  const fields = useMemo(
    () => {
      const filtered = (template?.fields || []).filter(
        ({ name, isHidden, isReadOnly, key }) =>
          !isHidden &&
          !filteredFiledNames.has(name) &&
          !isReadOnly &&
          !excludedFieldKeysSet.has((key ?? '').toLowerCase())
      );

      // Sort by displayOrder if available
      return [...filtered].sort((a, b) => {
        const DEFAULT_ORDER = Number.MAX_SAFE_INTEGER;
        const orderA = a.displayConfig?.displayOrder ?? DEFAULT_ORDER;
        const orderB = b.displayConfig?.displayOrder ?? DEFAULT_ORDER;
        return orderA - orderB;
      });
    },
    [template?.fields, excludedFieldKeysSet]
  );

  const allRefreshFields = useMemo(() => {
    return new Set([...refreshFields, ...defectLegendKeys]);
  }, [defectLegendKeys]);

  const verifyBlisterFields = useMemo(() => {
    if (!blister) return [];
    return [
      {
        configKey: 'verifiedMaxHeight' as const,
        stateKey: 'verified-max-height',
        getValue: () => Number(blister.verifiedMaxHeight) || blister.maxHeight || undefined,
        sourceUnit: UnitsEnum.Meter,
        displayAsUnit: UnitsEnum.Millimeter,
        roundToDecimal: 3,
      },
      {
        configKey: 'verifiedResidualThickness' as const,
        stateKey: 'verified-thickness',
        getValue: () =>
          Number(blister.verifiedResidualThickness) || blister.residualThickness?.value,
        sourceUnit: UnitsEnum.Meter,
        displayAsUnit: undefined,
        roundToDecimal: undefined,
      },
    ];
  }, [blister]);

  const formatValue = (
    value: string | undefined,
    unit: Maybe<UnitsEnum> | undefined,
    fieldName: string,
    isReadOnly: boolean
  ) => {
    if (!isReadOnly || !value || !unit || !formatValueWithUnit.has(fieldName)) return value;
    return (
      localiseMeasurement({
        unitSystem,
        sourceUnit: unit,
        value: Number(value),
        roundToDecimal: 2,
        showUnitLabel: true,
      }) ?? value
    );
  };
  return (
    <>
      {fields.map((field) => (
        <TemplateFieldRenderer
          inputAdormantHandlerMap={inputAdormantHandlerMap}
          key={`${field.id}${allRefreshFields.has(field.key ?? '') ? '-' + state[field.id] : ''}}`}
          isEditing
          templateField={field}
          onChange={(value) => {
            handleChange(field?.key ?? '', value);
            if (field.detailFieldKey) {
              handleChangeControlledField(field?.key ?? '', value);
            }
          }}
          value={formatValue(state[field?.key ?? ''], field.unit, field.name, field.isReadOnly)}
          disabled={allDisabled || field.isReadOnly || isEvaluatingFields}
        />
      ))}
      {formState === 'Verify' && blister && (
        <>
          {verifyBlisterFields.map((field) => {
            const config = blisterFieldConfig[field.configKey];
            if (config?.hidden) return null;

            const unitLabel = unitSystem === UnitSystemEnum.Imperial ? ' (inch)' : ' (mm)';

            return (
              <TemplateFieldRenderer
                inputAdormantHandlerMap={inputAdormantHandlerMap}
                key={field.stateKey}
                isEditing
                templateField={{
                  name: `${config?.name ?? field.configKey}${unitLabel}`,
                  id: field.stateKey,
                  isReadOnly: false,
                  isRequired: false,
                  options: [],
                  type: TemplateFieldTypeEnum.Numeric,
                  serverSide: false,
                }}
                onChange={(value) => {
                  const numValue = Number(value);
                  if (value === '' || isNaN(numValue)) return;
                  const valueInMeters =
                    unitSystem === UnitSystemEnum.Imperial
                      ? convertInchesToMeters(numValue)
                      : convertMillimetersToMeters(numValue);

                  handleChange(field.stateKey, valueInMeters.toString());
                }}
                value={
                  localiseMeasurement({
                    unitSystem,
                    value: field.getValue(),
                    sourceUnit: field.sourceUnit,
                    displayAsUnit: field.displayAsUnit,
                    roundToDecimal: field.roundToDecimal,
                    fallbackLabel: UNCERTAIN_PROFILE_LABEL,
                    showUnitLabel: false,
                  }) ?? UNCERTAIN_PROFILE_LABEL
                }
                disabled={allDisabled}
              />
            );
          })}
        </>
      )}
    </>
  );
};
